import {
  AppAnalysis,
  CodeWidgetAnalysis,
  Finding,
  RiskStatus,
  publishCheckFindings,
  publishCheckStatus,
} from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { CodeWidgetCheck } from '../widgets/CodeWidget/analysis/use-code-widget-check'

export type WidgetPublishCheck = {
  widget: CodeWidgetAnalysis
  label: string
  status: RiskStatus
  /** What decided `status`; empty when clear. */
  findings: Finding[]
}

export type PublishCheck = {
  /** `checking` shows the previous result while a scan runs; `timed-out` means the scan was stopped, `failed` that it could not run. */
  progress: CodeWidgetCheck['state']
  /** Null only before the first scan has finished. A scan that did not finish is amber, never green. */
  status: RiskStatus | null
  /** Worst first, so the card opens on what needs doing. */
  widgets: WidgetPublishCheck[]
  /** When the scan timed out: the widget it was reading, if it got that far. */
  stalledOn: string | null
}

const WORST_FIRST: Record<RiskStatus, number> = { flagged: 0, review: 1, clear: 2 }

export function toPublishCheck(check: CodeWidgetCheck): PublishCheck {
  if (check.state === 'timed-out') return { progress: check.state, status: 'review', widgets: [], stalledOn: check.widget }
  if (check.state === 'failed') return { progress: check.state, status: 'review', widgets: [], stalledOn: null }
  if (!check.analysis) return { progress: check.state, status: null, widgets: [], stalledOn: null }
  return { progress: check.state, ...fromAnalysis(check.analysis), stalledOn: null }
}

function fromAnalysis(analysis: AppAnalysis): Pick<PublishCheck, 'status' | 'widgets'> {
  const widgets = analysis.widgets
    .map((widget, index) => ({
      widget,
      label: widget.title ?? widget.widgetId ?? `Code Widget ${index + 1}`,
      status: publishCheckStatus(widget.risks),
      findings: publishCheckFindings(widget),
    }))
    .sort((a, b) => WORST_FIRST[a.status] - WORST_FIRST[b.status])

  return { status: publishCheckStatus(analysis.risks), widgets }
}
